import { FV, updateItemHighlightsByItems } from './share';
import { ReactNode, cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, FormItemProps, Radio } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import { stores2storeScopes } from '../share';
import { item2selectItem, selectItem2item } from './Items';
import { useTranslate } from '@tolgee/react';
import { useItemSelectContext } from './ItemSelectProvider';
import { DisabledProps } from 'views/Aide/Scenario/TaskForm/share';
import useSmartField from 'hooks/useSmartField';
import ItemHighlightsModal from './ItemHighlightsModal';
import Item from '@knowledge/common/components/Item';
import styled from '@emotion/styled';
import { useHelpContext } from '../HelpContext';

export function useGetRecommendItemsLabel() {
  const { t } = useTranslate();

  return {
    t,
    getRecommendItemsLabel: useCallback(
      (activityType: Knowledge.Activity.Type) => {
        return activityType === 'NEW_PRODUCT_ONSALE_ACTIVITY'
          ? t('上架商品')
          : activityType === 'RECOMMEND_HOT_PRODUCT_ACTIVITY'
          ? t('爆款商品')
          : t('recommend_item');
      },
      [t],
    ),
  };
}

export const notSupportItemsTypes = [
  'NEW_PRODUCT_ONSALE_ACTIVITY',
  'RECOMMEND_HOT_PRODUCT_ACTIVITY',
];

function RecommendItems({ itemsSelected, disabled }: { itemsSelected?: boolean } & DisabledProps) {
  const { t, getRecommendItemsLabel } = useGetRecommendItemsLabel();

  const { show } = useItemSelectContext();

  const {
    values: { activityType, stores, items, itemHighlights },
    setValues,
  } = useFormikContext<FV>();

  const label = getRecommendItemsLabel(activityType);

  const {
    field: { value, onChange, onBlur },
    meta: { touched, error },
  } = useSmartField<FV['recommendItems']>('recommendItems', (value): string | undefined =>
    (recommend || itemsSelected) && itemsSelected !== false && !value?.length
      ? t('department.请选择x', { x: label })
      : undefined,
  );

  const internalItemId = useMemo(() => items.map((item) => String(item.itemInternalId)), [items]);
  const storeScopes = useMemo(() => stores2storeScopes(stores), [stores]);

  const [selectItems, setSelectItems] = useState(
    () => (value?.map(item2selectItem) || []) as unknown as ItemSelectV2.Item[],
  );

  const mastRecommend = ['NEW_PRODUCT_ONSALE_ACTIVITY', 'RECOMMEND_HOT_PRODUCT_ACTIVITY'].includes(
    activityType,
  );

  const [recommend, setRecommend] = useState(mastRecommend || Boolean(value?.length));

  const [highlightModalVisible, setHighlightModalVisible] = useState(false);

  const {
    stepState: [, setHelpStep],
  } = useHelpContext();
  const showHelp = () => {
    if (notSupportItemsTypes.includes(activityType)) {
      setHelpStep('适用商品');
    }
  };

  const refsCurrent = {
    value,
    onChange,
    itemHighlights,
    setValues,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (stores.length === 0) {
      return;
    }

    const { value, onChange } = refs.current;

    if (!value) {
      return;
    }

    const nextItems = value.filter((_) =>
      stores.some(({ storeId }) => `${storeId}` === `${_.storeId}`),
    );

    if (nextItems.length === value.length) {
      return;
    }

    onChange(nextItems);

    setSelectItems((items) =>
      items.filter(({ internalStoreId }) =>
        stores.some(({ storeId }) => `${storeId}` === `${internalStoreId}`),
      ),
    );
  }, [stores]);

  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    const { value, onChange } = refs.current;

    if (!value) {
      return;
    }

    let shouldUpdate = false;

    const nextRecommendItems = value.reduce<typeof value>((res, cur) => {
      const item = items.find((_) => _.itemInternalId === cur.itemInternalId);

      if (!item) {
        shouldUpdate = true;

        return res;
      }

      const skus = cur.skus.filter(({ skuId }) => item.skus.some((_) => _.skuId === skuId));

      if (skus.length === cur.skus.length) {
        res.push(cur);

        return res;
      }

      shouldUpdate = true;

      res.push({
        ...cur,
        skus,
      });

      return res;
    }, []);

    if (shouldUpdate) {
      onChange(nextRecommendItems);

      setSelectItems((selectItems) =>
        selectItems.reduce<typeof selectItems>((res, cur) => {
          const item = items.find((_) => _.itemInternalId === Number(cur.internalItemId));

          if (!item) {
            return res;
          }

          const skuInfo = cur.skuInfo.filter(({ skuId }) =>
            item.skus.some((_) => _.skuId === skuId),
          );

          if (skuInfo.length === cur.skuInfo.length) {
            res.push(cur);
          } else {
            res.push({
              ...cur,
              skuInfo,
            });
          }

          return res;
        }, []),
      );
    }
  }, [items]);

  useEffect(() => {
    if (!mastRecommend) return;

    const { itemHighlights, setValues } = refs.current;

    const next = updateItemHighlightsByItems(value || [], itemHighlights || []);

    if (next !== itemHighlights) {
      setValues((values) => ({
        ...values,
        itemHighlights: next,
      }));
    }
  }, [value, mastRecommend]);

  const { noItemStores, ...formItemProps } = useMemo<
    FormItemProps & { noItemStores?: Knowledge.Activity.Store[] }
  >(() => {
    if (!recommend || itemsSelected === false) {
      return {};
    }

    if (touched && error) {
      return {
        help: error,
        validateStatus: 'error',
      };
    }

    if (!value || !value.length) {
      return label === t('recommend_item')
        ? {
            help: t('activity_recommend_item_tips'),
          }
        : {};
    }

    const noItemStores = stores.filter((store) =>
      value.every((item) => item.storeId !== store.storeId),
    );

    if (noItemStores.length > 0) {
      return {
        noItemStores,
        help: t('activity_x_store_no_recommend_item_tips', { count: noItemStores.length }),
        validateStatus: 'warning',
      };
    }

    return {};
  }, [error, itemsSelected, recommend, stores, touched, value, label, t]);

  const withFormItem = (...nodes: ReactNode[]) => (
    <Form.Item label={label} required={mastRecommend} {...formItemProps}>
      {...nodes}
    </Form.Item>
  );

  if (itemsSelected === false) {
    return withFormItem(t('no_recommend_item'));
  }

  const onShow = (highlightOnConfirm = true) => {
    show({
      storeType: 'ALL',
      storeScopes,
      skuDisabled: true,
      sideStoreEnable: true,
      defaultFoldSku: true,
      internalItemId,
      value: selectItems,
      initialStoreScopes: noItemStores ? stores2storeScopes(noItemStores) : undefined,
      onChange: (value) => {
        onChange(value.map(selectItem2item));
        setSelectItems(value);
      },
      onCancel: () => setTimeout(() => onBlur()),
      confirmRender: !(mastRecommend && highlightOnConfirm)
        ? undefined
        : (confirm, onConfirm) => {
            return [
              cloneElement(confirm, { type: undefined }),
              <Button
                type="primary"
                onClick={() => {
                  onConfirm();
                  setHighlightModalVisible(true);
                }}
              >
                {t('确认选择，添加商品亮点')}
              </Button>,
            ];
          },
    });

    showHelp();
  };

  const action = !value?.length ? (
    <Button
      disabled={disabled}
      style={{ paddingLeft: 0 }}
      type="link"
      icon={<PlusOutlined />}
      onClick={() => onShow()}
    >
      {t('活动机器人-选择商品')}
    </Button>
  ) : (
    <div>
      {t('selected_items', { count: value.length })}
      <Button disabled={disabled} type="link" icon={<EditOutlined />} onClick={() => onShow()} />
    </div>
  );

  if (mastRecommend) {
    const itemHighlightsModal = value && itemHighlights && (
      <ItemHighlightsModal
        visible={highlightModalVisible}
        onCancel={() => setHighlightModalVisible(false)}
        itemLabel={label}
        recommendItems={value}
        itemHighlights={itemHighlights}
        onEditItems={() => onShow(false)}
        onItemHighlightsChange={(itemHighlights) =>
          setValues((values) => ({
            ...values,
            itemHighlights,
          }))
        }
      />
    );

    const recommendItemsField = withFormItem(
      <Container>
        {value?.length ? (
          <section>
            {value?.map((item) => {
              const { itemInternalId } = item;
              const highlight = itemHighlights?.find(({ appliedItems }) =>
                appliedItems.some((item) => item.itemInternalId === itemInternalId),
              )?.highlight;
              return (
                <Item
                  key={itemInternalId}
                  width={40}
                  item={item}
                  extra={
                    highlight ? (
                      <div className="line-clamp-1" title={highlight}>
                        {highlight}
                      </div>
                    ) : undefined
                  }
                />
              );
            })}
          </section>
        ) : null}
        {action}
        {itemHighlightsModal}
      </Container>,
    );

    const unHighlightItemsCount = itemHighlights?.reduce((res, { highlight, appliedItems }) => {
      return res + (highlight ? 0 : appliedItems.length);
    }, 0);

    const itemHighlightsField = value?.length ? (
      <Form.Item label={t('商品亮点')}>
        <Button
          disabled={disabled}
          className="!pl-0"
          type="text"
          icon={<EditOutlined />}
          onClick={() => setHighlightModalVisible(true)}
        >
          {t('调整亮点')}
        </Button>
        {unHighlightItemsCount ? (
          <div style={{ color: 'var(--fett-surface-error-text)' }}>
            {t(`x个商品未配置亮点${unHighlightItemsCount > 1 ? '_other' : ''}`, {
              x: unHighlightItemsCount,
            })}
          </div>
        ) : null}
      </Form.Item>
    ) : null;

    return (
      <>
        {recommendItemsField}
        {itemHighlightsField}
      </>
    );
  }

  if (itemsSelected === true) {
    return withFormItem(action);
  }

  return withFormItem(
    <div>
      <div className="pt-[6px] pb-[6px]">
        <Radio
          disabled={disabled}
          checked={recommend}
          onChange={({ target: { checked } }) => {
            setRecommend(checked);
            setTimeout(() => onChange([]));
            showHelp();
          }}
        >
          {t('has_recommend_item')}
        </Radio>
        <Radio
          disabled={disabled}
          checked={!recommend}
          onChange={({ target: { checked } }) => {
            setRecommend(!checked);
            setSelectItems([]);
            setTimeout(() => onChange([]));
            showHelp();
          }}
        >
          {t('no_recommend_item')}
        </Radio>
      </div>
      {!recommend ? null : (
        <div className="w-[352px] bg-[var(--fett-erp-card-bg)] px-[8px] py-[4px]">{action}</div>
      )}
    </div>,
  );
}

const Container = styled.div`
  background: var(--fett-erp-card-bg);
  > section {
    max-height: 216px;
    margin-bottom: 12px;
    overflow: auto;
    > div {
      padding: 12px;
      background: var(--fett-bg-panel);
      margin-bottom: 12px;
      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }
`;

export default RecommendItems;
